import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { AbstractOnboardingPageComponent } from 'src/app/core/abstracts/components/onboarding-page.abstract';
import { EventTypes } from 'src/app/core/enums/event.enum';
import { IPageContent } from 'src/app/core/interfaces/common/content/page-content.interface';
import { IMultiOptions } from 'src/app/core/interfaces/onboarding/business-profile/business-multi-options.interface';
import { IBusinessProfile } from 'src/app/core/interfaces/onboarding/business-profile/business-profile.interface';
import { ITransition } from 'src/app/core/interfaces/onboarding/page-state/transition.interface';
import { PageState } from 'src/app/core/models/page-state/page-state.model';
import { BusinessProfileService } from 'src/app/core/services/onboarding/business-profile.service';
import { EnrollmentExtendedService } from 'src/app/core/services/onboarding/enrollment-extended.service';
import { PageStateService } from 'src/app/core/services/onboarding/page-state.service';
import { ServersideAlertService } from 'src/app/core/services/onboarding/serverside-alert.service';
import { IAppState } from 'src/app/core/store/common/interfaces/app-state.interface';
import { SupportMethodsAction } from 'src/app/core/store/onboarding/actions/business-profile.actions';
import { selectSupportMethod } from 'src/app/core/store/onboarding/reducers+selectors/business-profile.selector';
import { destructSubscriptionArray } from 'src/app/core/ui-helpers/functions/destruct-subscription-array';
import { watchTabletThreshold } from 'src/app/core/ui-helpers/functions/watch-tablet-threshold';
import { ContentIDConstants } from './ContentIDConstants';

@Component({
  selector: 'app-support-method',
  templateUrl: './support-method.component.html',
  styleUrls: ['./support-method.component.scss'],
})
export class SupportMethodComponent
  extends AbstractOnboardingPageComponent
  implements OnInit, OnDestroy
{
  isTablet: boolean;
  supportMethodData: IMultiOptions[] = [];

  backState: PageState;
  nextState: PageState;
  skipState: PageState;
  homeState: PageState;
  othersSelected = false;
  pageStateId: string;

  questionText: string;
  properties$: Observable<any>;
  subscriptions: Subscription[] = [];
  payload: IBusinessProfile;

  constructor(
    private route: ActivatedRoute,
    private ps: PageStateService,
    private ees: EnrollmentExtendedService,
    private bf: BusinessProfileService,
    private notificationService: ServersideAlertService,
    private store: Store<IAppState>
  ) {
    super();
    this.subscriptions.push(
      watchTabletThreshold((isTablet: boolean) => {
        this.isTablet = isTablet;
      })
    );
  }

  async ngOnInit(): Promise<void> {
    this.subscriptions.push(
      this.route.data.subscribe(async (data) => {
        const { transitions } = data['data'];
        const { contentDict } = data['data'];
        this.populatePageStates(transitions);
        this.populatePageContent(contentDict);
        this.subscriptions.push(
          this.route.queryParams.subscribe((params) => {
            this.ees.updateBusinessProfileStateId(
              params['stateId'],
              this.homeState.id
            );
            this.pageStateId = params['stateId'];
          })
        );
        this.supportMethodData = data['options'];
        const props$ = this.store.select(selectSupportMethod);
        this.subscriptions.push(
          props$.subscribe((props) => {
            if (props.supportMethods.length === 0) return;
            this.supportMethodData = props.supportMethods;
          })
        );
      })
    );
    this.payload = this.bf.getPayload();

    // Update payload with pagestate id of current page
    this.payload.PageStateId = this.pageStateId;
  }

  ngOnDestroy(): void {
    destructSubscriptionArray(this.subscriptions);
  }

  async submit() {
    let anySelected = false;
    const { length } = this.supportMethodData;
    for (let i = 0; i < length; i += 1) {
      if (this.supportMethodData[i].selected) anySelected = true;
    }
    if (!anySelected) {
      this.notificationService.reportAlert(
        ['You must select one option.'],
        'warn'
      );
      return false;
    }

    this.store.dispatch(
      SupportMethodsAction({
        supportMethods: this.supportMethodData,
      })
    );
    this.payload.SupportMethods = this.supportMethodData;
    const response = await this.bf.submitBusinessProfileData(this.payload);
    return response.isSuccessful;
  }

  populatePageStates(transitions: ITransition[]): void {
    this.backState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.BACK,
      transitions
    );
    this.nextState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.NEXT,
      transitions
    );
    this.skipState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.SKIP,
      transitions
    );
    this.homeState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.HOME,
      transitions
    );
  }

  populatePageContent(contentDict: Map<number, IPageContent>): void {
    this.questionText =
      contentDict.get(ContentIDConstants.Question)?.displayText ??
      'Are you looking for a grant or a loan?';
  }

  async handleOutput(id: number) {
    const tempArr = this.supportMethodData.map((method: IMultiOptions) => {
      if (method.id === id) {
        return {
          id: method.id,
          name: method.name,
          value: method.value,
          selected: !method.selected,
        };
      }
      return method;
    });
    this.supportMethodData = tempArr;

    this.store.dispatch(
      SupportMethodsAction({
        supportMethods: this.supportMethodData,
      })
    );
  }
}
